/**
 * "El profesor pasa" — random immersion banners that pop up during the
 * written exam. Designed to recreate the feeling of being inside the
 * real Italian medical recognition exam: nosy professor, sneaky neighbor
 * trying to copy, dropped answer sheet... All static text — zero LLM
 * runtime cost.
 *
 * Behavior:
 *  - One banner appears every ~3-5 min, picked at random with weight.
 *  - Each banner has IT + ES + a tone (warning / neutral / funny).
 *  - Auto-dismisses after 7 seconds OR on click.
 *  - The user cannot stack them — only one banner at a time.
 */
import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, AlertTriangle, Coffee, Volume2, X } from "lucide-react";

// Banners ordered roughly by "this could realistically happen at minute X".
// Tones: warning (red-ish) / neutral (yellow) / funny (violet).
export const EXAM_INCIDENTS = [
  {
    key: "prof_passes",
    icon: Eye, tone: "warning",
    title_it: "Il professore passa accanto a te",
    title_es: "El profesor pasa al lado tuyo",
    body_it: "Si ferma. Ti guarda. Sembra pensare che ti stai copiando. Continua a rispondere come se nulla fosse.",
    body_es: "Se detiene. Te mira. Parece pensar que te estás copiando. Seguí respondiendo como si nada.",
  },
  {
    key: "neighbor_copies",
    icon: AlertTriangle, tone: "warning",
    title_it: "Il compagno di banco ti spia",
    title_es: "El compañero de al lado te está espiando",
    body_it: "Si è inclinato per leggere il tuo foglio. Copri le risposte con la mano: se il professore lo vede, sei TU quello nei guai.",
    body_es: "Se inclinó para ver tu hoja. Cubrí las respuestas con la mano: si el profesor lo ve, vos sos el que se mete en problemas.",
  },
  {
    key: "paper_drops",
    icon: AlertTriangle, tone: "warning",
    title_it: "Cade un foglietto con risposte vicino al tuo banco",
    title_es: "Cae un papelito con respuestas cerca de tu banco",
    body_it: "Non sai chi l'ha lanciato. Calpestalo prima che il professore pensi che sia tuo. NON guardarlo.",
    body_es: "No sabés quién lo tiró. Pisalo antes de que el profesor piense que es tuyo. NO lo mires.",
  },
  {
    key: "prof_sneeze",
    icon: Volume2, tone: "neutral",
    title_it: "Il professore tossisce forte",
    title_es: "El profesor tose fuerte",
    body_it: "Tutti alzano la testa. Concentrazione persa per 5 secondi. Respira e torna alla domanda.",
    body_es: "Todos levantan la cabeza. Concentración perdida por 5 segundos. Respirá y volvé a la pregunta.",
  },
  {
    key: "phone_rings",
    icon: Volume2, tone: "neutral",
    title_it: "Squilla un cellulare in fondo all'aula",
    title_es: "Suena un celular al fondo del aula",
    body_it: "Il professore si infuria. Espelle qualcuno. Non guardare: pensa al tuo esame.",
    body_es: "El profesor se enfurece. Echa a alguien. No mires: pensá en tu examen.",
  },
  {
    key: "ac_loud",
    icon: Volume2, tone: "funny",
    title_it: "L'aria condizionata fa un rumore assordante",
    title_es: "El aire acondicionado hace un ruido ensordecedor",
    body_it: "Non puoi pensare. Ignoralo o chiedere di spostarti? Decidi in 3 secondi.",
    body_es: "No podés pensar. ¿Lo ignorás o pedís cambiarte? Decidí en 3 segundos.",
  },
  {
    key: "neighbor_pen",
    icon: AlertTriangle, tone: "funny",
    title_it: "Il compagno chiede una penna in prestito",
    title_es: "El compañero te pide una birome prestada",
    body_it: "Se lo aiuti, il professore ti vede e potrebbe espellerti. Se non lo aiuti, ti odia per il resto della giornata.",
    body_es: "Si lo ayudás, el profesor te ve y podría echarte. Si no lo ayudás, te odia por el resto del día.",
  },
  {
    key: "stomach",
    icon: Coffee, tone: "funny",
    title_it: "Ti brontola lo stomaco",
    title_es: "Te suena la panza",
    body_it: "Tutti ti guardano. Avresti dovuto fare colazione. Concentrati.",
    body_es: "Todos te miran. Tendrías que haber desayunado. Concentrate.",
  },
  {
    key: "prof_yells",
    icon: Volume2, tone: "warning",
    title_it: "Il professore urla: «Chi parla è fuori!»",
    title_es: "El profesor grita: «¡El que hable está afuera!»",
    body_it: "Silenzio totale. Cuore in gola. Continua a rispondere.",
    body_es: "Silencio total. El corazón te late fuerte. Seguí respondiendo.",
  },
  {
    key: "watch_check",
    icon: Eye, tone: "neutral",
    title_it: "Controlla l'orologio del muro",
    title_es: "Revisá el reloj de la pared",
    body_it: "Sta correndo. Le risposte 'pulite' valgono di più di quelle 'tutte'. Scegli bene.",
    body_es: "Está corriendo. Las respuestas 'limpias' valen más que las 'todas'. Elegí bien.",
  },
  {
    key: "prof_collect_warning",
    icon: AlertTriangle, tone: "warning",
    title_it: "Il professore inizia a raccogliere i fogli dei primi banchi",
    title_es: "El profesor empieza a pedir las hojas de los primeros bancos",
    body_it: "Avete poco tempo. Le risposte vuote diventeranno errori se non chiudete il modulo.",
    body_es: "Tenés poco tiempo. Las respuestas vacías se cuentan como error si no cerrás el módulo.",
  },
  {
    key: "lights_flicker",
    icon: AlertTriangle, tone: "funny",
    title_it: "Le luci dell'aula sfarfallano",
    title_es: "Las luces del aula parpadean",
    body_it: "Per un secondo pensi sia un sogno. Non lo è. Continua.",
    body_es: "Por un segundo pensás que es un sueño. No lo es. Seguí.",
  },
];

// Picks a random incident, optionally excluding ones we've already seen.
export function pickRandomIncident(seenKeys = new Set()) {
  const pool = EXAM_INCIDENTS.filter((i) => !seenKeys.has(i.key));
  const fromPool = pool.length > 0 ? pool : EXAM_INCIDENTS;
  return fromPool[Math.floor(Math.random() * fromPool.length)];
}

const TONE_STYLES = {
  warning: {
    bg: "linear-gradient(135deg, #FCE9EE 0%, #FFF1F4 100%)",
    border: "rgba(158,42,72,0.45)",
    iconBg: "rgba(158,42,72,0.18)",
    iconColor: "#9E2A48",
    titleColor: "#7A1A36",
    bodyColor: "#5C2032",
  },
  neutral: {
    bg: "linear-gradient(135deg, #FFF7E6 0%, #FFEED1 100%)",
    border: "rgba(180,83,9,0.35)",
    iconBg: "rgba(180,83,9,0.15)",
    iconColor: "#B45309",
    titleColor: "#7A4F00",
    bodyColor: "#5A3A00",
  },
  funny: {
    bg: "linear-gradient(135deg, #F0EAFF 0%, #E0D4FA 100%)",
    border: "rgba(90,63,192,0.4)",
    iconBg: "rgba(90,63,192,0.18)",
    iconColor: "#5A3FC0",
    titleColor: "#3A2A8A",
    bodyColor: "#2A1F4A",
  },
};

/**
 * ExamIncidentBanner — controlled component.
 *
 * Props:
 *   incident: one of EXAM_INCIDENTS (or null = no banner shown)
 *   language: "it" | "es" (forced to "it" in the written-exam runner)
 *   onDismiss: () => void
 *   pulse: when true, the banner subtly pulses to demand attention.
 *
 * Phase 6.3 behaviour change: the banner does NOT auto-dismiss. It stays
 * on screen until the user explicitly clicks ✕. The pilot-simulator
 * metaphor: distractions don't go away by themselves. The escalating
 * frequency is handled by the caller (RunnerView), not here.
 */
export function ExamIncidentBanner({ incident, language = "it", onDismiss,
                                     pulse = false }) {
  if (!incident) return null;
  const tone = TONE_STYLES[incident.tone] || TONE_STYLES.warning;
  const Icon = incident.icon || AlertTriangle;
  const title = language === "es" ? incident.title_es : incident.title_it;
  const body = language === "es" ? incident.body_es : incident.body_it;

  return (
    <AnimatePresence>
      <motion.div
        key={incident.key}
        initial={{ opacity: 0, y: -16, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -16, scale: 0.96 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className={[
          "fixed top-20 left-1/2 -translate-x-1/2 z-[60] max-w-md w-[92%] mx-auto",
          pulse ? "animate-pulse" : "",
        ].join(" ")}
        data-testid="exam-incident-banner"
        data-incident-key={incident.key}
      >
        <div className="rounded-2xl border px-4 py-3.5 shadow-2xl backdrop-blur-md"
             style={{ background: tone.bg, borderColor: tone.border,
                      boxShadow: `0 22px 50px -10px ${tone.iconColor}55` }}>
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-xl grid place-items-center shrink-0"
                 style={{ background: tone.iconBg }}>
              <Icon className="h-5 w-5" style={{ color: tone.iconColor }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[14px] leading-tight"
                 style={{ color: tone.titleColor }}>
                {title}
              </p>
              <p className="text-[13px] mt-1 leading-relaxed"
                 style={{ color: tone.bodyColor }}>
                {body}
              </p>
              <button onClick={onDismiss}
                      data-testid="exam-incident-dismiss"
                      className="mt-2 text-[12px] font-semibold underline"
                      style={{ color: tone.iconColor }}>
                {language === "es" ? "Lo capisco — continua" : "Ho capito — continua"}
              </button>
            </div>
            <button onClick={onDismiss}
                    aria-label="Chiudi"
                    className="p-1 rounded-md shrink-0 hover:bg-white/40"
                    style={{ color: tone.iconColor }}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
